import { ButtonContent } from '../../ui/ButtonContent';
import React, { useState } from 'react';
import { AnimatedLabel } from '../../ui/AnimatedLabel';
import { AnimatedPresence } from '../../ui/AnimatedPresence';
import { useSearchParams } from 'react-router-dom';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { GoogleIcon } from '../../icons/GoogleIcon';
import { GitHubIcon } from '../../icons/GitHubIcon';
import { LinkedInFilledIcon } from '../../icons/LinkedInFilledIcon';
import { IconMail } from '@tabler/icons-react';
import type { UserIdentity, Provider } from '@supabase/supabase-js';

/** OAuth providers users can link/unlink. */
const OAUTH_PROVIDERS: { id: Provider; icon: React.ReactNode; label: string }[] = [
  { id: 'google', icon: <GoogleIcon className="w-5 h-5" />, label: 'Google' },
  { id: 'github', icon: <GitHubIcon className="w-5 h-5" />, label: 'GitHub' },
  { id: 'linkedin_oidc' as Provider, icon: <LinkedInFilledIcon className="w-5 h-5" />, label: 'LinkedIn' },
];

/** Map provider IDs to display info (includes email for display purposes). */
const PROVIDER_MAP: Record<string, { icon: React.ReactNode; label: string }> = {
  google: { icon: <GoogleIcon className="w-5 h-5" />, label: 'Google' },
  github: { icon: <GitHubIcon className="w-5 h-5" />, label: 'GitHub' },
  linkedin_oidc: { icon: <LinkedInFilledIcon className="w-5 h-5" />, label: 'LinkedIn' },
  email: { icon: <IconMail className="w-5 h-5" />, label: 'Email (OTP)' },
};

/**
 * Account section of the Settings page.
 * Shows connected sign-in methods, email, and account deletion.
 */
export const AccountSection: React.FC = () => {
  const { user, supabase } = useSupabase();
  const [, setSearchParams] = useSearchParams();

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [linkingProvider, setLinkingProvider] = useState<string | null>(null);
  const [unlinkMessage, setUnlinkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const identities = user?.identities ?? [];
  const connectedProviders = new Set(identities.map((i) => i.provider));
  const hasOAuth = identities.some((i) => i.provider !== 'email');
  const oauthIdentities = identities.filter((i) => i.provider !== 'email');
  /** Can only unlink an OAuth provider if user has another way to sign in (email or another OAuth). */
  const canUnlink = identities.length > 1;
  /** OAuth providers the user hasn't connected yet. */
  const availableToLink = OAUTH_PROVIDERS.filter((p) => !connectedProviders.has(p.id));

  /** Unlink a sign-in provider identity. */
  const handleUnlink = async (identity: UserIdentity) => {
    setUnlinkingId(identity.id);
    setUnlinkMessage(null);

    try {
      const { error } = await supabase.auth.unlinkIdentity(identity);
      if (error) throw error;

      setUnlinkMessage({ type: 'success', text: `${PROVIDER_MAP[identity.provider]?.label ?? identity.provider} has been unlinked.` });
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'AccountSection.handleUnlink',
        { userId: user?.id, provider: identity.provider }
      );
      setUnlinkMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to unlink provider. Please try again.',
      });
    } finally {
      setUnlinkingId(null);
    }
  };

  /** Link an additional OAuth provider to this account. */
  const handleLink = async (provider: Provider) => {
    setLinkingProvider(provider);
    setUnlinkMessage(null);

    try {
      const redirectTo = `${window.location.origin}/admin/settings`;
      const options: { redirectTo: string; queryParams?: Record<string, string> } = { redirectTo };

      if (provider === 'linkedin_oidc') {
        options.queryParams = {
          access_type: 'offline',
          prompt: 'consent',
          scope: 'openid email profile',
        };
      } else if (provider === 'google') {
        options.queryParams = {
          scope: 'openid email profile',
          access_type: 'offline',
          prompt: 'consent',
        };
      }

      const { error } = await supabase.auth.linkIdentity({ provider, options });
      if (error) throw error;
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'AccountSection.handleLink',
        { userId: user?.id, provider }
      );
      setUnlinkMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to link provider. Please try again.',
      });
      setLinkingProvider(null);
    }
  };

  /** Submit email change request. */
  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setEmailSaving(true);
    setEmailMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;

      setEmailMessage({
        type: 'success',
        text: 'A confirmation link has been sent to your new email address.',
      });
      setShowEmailForm(false);
      setNewEmail('');
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'AccountSection.handleChangeEmail',
        { userId: user?.id }
      );
      setEmailMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update email. Please try again.',
      });
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground mb-6">Account</h2>

      {/* Connected Sign-in Methods */}
      <div className="mb-8">
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-3">
          Connected Sign-in Methods
        </label>
        <div className="space-y-2">
          {/* Connected OAuth providers - with Unlink option */}
          {oauthIdentities.map((identity) => {
            const provider = PROVIDER_MAP[identity.provider] ?? {
              icon: <IconMail className="w-5 h-5" />,
              label: identity.provider,
            };
            const email = (identity.identity_data as Record<string, unknown>)?.email as string | undefined;

            return (
              <div
                key={identity.id}
                className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="shrink-0 text-muted-foreground">{provider.icon}</span>
                  <div className="min-w-0">
                    <span className="text-sm text-foreground font-medium">{provider.label}</span>
                    {email && (
                      <p className="text-xs text-muted-foreground truncate">{email}</p>
                    )}
                  </div>
                </div>
                <button aria-busy={unlinkingId === identity.id}
                  onClick={() => handleUnlink(identity)}
                  disabled={!canUnlink || unlinkingId === identity.id}
                  title={!canUnlink ? 'Cannot remove your only sign-in method' : `Unlink ${provider.label}`}
                  className="text-xs text-muted-foreground hover:text-danger transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0 ml-3"
                >
                  <ButtonContent loading={unlinkingId === identity.id}>Unlink</ButtonContent>
                </button>
              </div>
            );
          })}

          {/* Available OAuth providers to link */}
          {availableToLink.map((provider) => (
            <div
              key={provider.id}
              className="flex items-center justify-between p-3 bg-background border border-border/50 border-dashed rounded-lg"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 text-muted-foreground">{provider.icon}</span>
                <span className="text-sm text-muted-foreground">{provider.label}</span>
              </div>
              <button aria-busy={linkingProvider === provider.id}
                onClick={() => handleLink(provider.id)}
                disabled={linkingProvider === provider.id}
                className="text-xs text-volt-soft light:text-volt hover:text-volt-soft transition-colors disabled:opacity-50 shrink-0 ml-3"
              >
                <ButtonContent loading={linkingProvider === provider.id}>Link</ButtonContent>
              </button>
            </div>
          ))}
        </div>

        {unlinkMessage && (
          <p className={`text-sm mt-2 ${unlinkMessage.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {unlinkMessage.text}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="mb-8">
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2">
          Email Address
        </label>
        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground">{user?.email}</span>
          {!hasOAuth && !showEmailForm && (
            <button
              onClick={() => setShowEmailForm(true)}
              className="text-xs text-volt-soft light:text-volt hover:text-volt-soft transition-colors"
            >
              Change email
            </button>
          )}
          {hasOAuth && (
            <span className="text-xs text-muted-foreground">Managed by sign-in provider</span>
          )}
        </div>

        <AnimatedPresence show={!hasOAuth && showEmailForm}>
          <form onSubmit={handleChangeEmail} className="mt-3 flex items-end gap-3">
            <div className="flex-1">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="New email address"
                required
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-subtle-foreground focus:outline-none focus:border-volt"
              />
            </div>
            <button aria-busy={emailSaving}
              type="submit"
              disabled={emailSaving || !newEmail.trim()}
              className="px-4 py-2 button-accent font-bold text-sm rounded-lg transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50"
            >
              <ButtonContent loading={emailSaving}>Update</ButtonContent>
            </button>
            <button
              type="button"
              onClick={() => { setShowEmailForm(false); setNewEmail(''); }}
              className="px-4 py-2 text-muted-foreground text-sm rounded-lg hover:text-foreground hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
          </form>
        </AnimatedPresence>

        <AnimatedPresence show={!!emailMessage}>
          <p className={`text-sm mt-2 transition-colors ${emailMessage?.type === 'success' ? 'text-success' : 'text-danger'}`}>
            <AnimatedLabel text={emailMessage?.text ?? ''} multiline />
          </p>
        </AnimatedPresence>
      </div>

      {/* Delete Account */}
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2">
          Danger Zone
        </label>
        <div className="p-4 bg-wash-danger rounded-lg">
          <p className="text-sm text-foreground/80 mb-3">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 border border-danger/40 text-danger text-sm rounded-lg hover:bg-danger/10 transition-colors"
            >
              Delete Account
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-danger">
                To delete your account, please contact our support team.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSearchParams({ report: 'account_issue', message: 'I would like to request the deletion of my account and all associated data.' });
                    setTimeout(() => {
                      document.getElementById('support')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 50);
                  }}
                  className="px-4 py-2 bg-danger text-danger-foreground text-sm font-bold rounded-lg hover:opacity-90 transition-colors"
                >
                  Contact Support
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-muted-foreground text-sm rounded-lg hover:text-foreground hover:bg-surface-hover transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
